import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleChevronDown, CircleChevronUp } from "lucide-react";
import { VirtualTable, Spinner, SelectField } from "@/components/reusable";
import type { ParsedWorksheet } from "@/types";
import { cn, formatDateTime } from "@/lib/utils";
import {
  allRegionReportTypesSet,
  countSelectedMappingWorksheets,
  getSelectedMappingWorksheets,
  getWorksheetKey,
  isWorksheetSelected,
} from "@/lib/mappingRegions";

function globalConfidenceColorClass(confidence: number): string {
  if (confidence >= 0.85) {
    return "text-emerald-600";
  }
  if (confidence >= 0.7) {
    return "text-amber-600";
  }
  return "text-red-600";
}

function sectionKindLabel(ws: ParsedWorksheet): "mapping" | "non-mapping" | "ambiguity" {
  if (ws.previewCategory === "ambiguity") {
    return "ambiguity";
  }
  if (ws.previewCategory === "non_mapping" || ws.isMetadata) {
    return "non-mapping";
  }
  return "mapping";
}

interface RegionIncludeCheckboxProps {
  previewKey: string;
  included: boolean;
  ariaLabel: string;
  onToggle: () => void;
}

function RegionIncludeCheckbox({
  previewKey,
  included,
  ariaLabel,
  onToggle,
}: RegionIncludeCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={included}
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={`structure-chk-${previewKey}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center self-center rounded-sm border-2 bg-[var(--color-bg-primary)] shadow-sm transition-colors",
        "outline outline-2 outline-offset-2 focus-visible:outline-[var(--color-text-primary)] focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)] focus-visible:ring-offset-2",
        included
          ? "border-[var(--color-text-primary)] bg-[var(--color-button-primary-bg)] text-white outline-[var(--color-text-primary)]"
          : "border-[var(--color-text-primary)] outline-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]"
      )}
    >
      {included ? (
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
        </svg>
      ) : null}
    </button>
  );
}

export interface ConfirmStructurePanelProps {
  fileName: string;
  schemaName: string;
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  onMappingSelectionChange: (previewKey: string, included: boolean) => void;
  requiresReportType?: boolean;
  reportType?: string | null;
  detectedType?: string | null;
  allowedReportTypes?: string[];
  isClassifying?: boolean;
  onReportTypeChange?: (value: string | null) => void;
  regionReportTypes?: Record<string, string | null>;
  onRegionReportTypeChange?: (regionKey: string, value: string | null) => void;
  isUploading?: boolean;
  requiresReview?: boolean;
  reviewReason?: string | null;
  reviewThreshold?: number;
  globalConfidence?: number | null;
  ingestionTriggerSkipped?: boolean;
}

export function ConfirmStructurePanel({
  fileName,
  schemaName,
  worksheets,
  mappingSelection,
  onMappingSelectionChange,
  requiresReportType = false,
  reportType = null,
  detectedType = null,
  allowedReportTypes = [],
  isClassifying = false,
  onReportTypeChange,
  regionReportTypes = {},
  onRegionReportTypeChange,
  isUploading = false,
  requiresReview = false,
  reviewReason = null,
  reviewThreshold = 0.75,
  globalConfidence = null,
  ingestionTriggerSkipped = false,
}: ConfirmStructurePanelProps) {
  const { t } = useTranslation();
  const worksheetSignature = useMemo(
    () => worksheets.map((ws, index) => getWorksheetKey(ws, index)).join("|"),
    [worksheets]
  );
  // Sections start collapsed so long files don't force endless scrolling;
  // a lone region expands since there is nothing else to scan past.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
  const singleSectionKey = useMemo(
    () => (worksheets.length === 1 ? getWorksheetKey(worksheets[0]!, 0) : null),
    [worksheets]
  );

  useEffect(() => {
    setExpandedKeys(singleSectionKey ? new Set([singleSectionKey]) : new Set());
  }, [worksheetSignature, singleSectionKey]);

  const toggleExpanded = (previewKey: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(previewKey)) {
        next.delete(previewKey);
      } else {
        next.add(previewKey);
      }
      return next;
    });
  };

  const fileTypeOptions = allowedReportTypes.map((rt) => {
    const label = t(`upload.fileType.${rt}`, {
      defaultValue: rt.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });
    return { value: rt, label };
  });

  const selectedWorksheets = getSelectedMappingWorksheets(worksheets, mappingSelection);
  const selectedCount = countSelectedMappingWorksheets(worksheets, mappingSelection);
  const selectedRowCount = selectedWorksheets.reduce((sum, ws) => sum + ws.totalRows, 0);
  const skippedCount = worksheets.length - selectedCount;
  const isMultiRegionPreview = selectedCount > 1;

  const hasConfirmedType = !!reportType && reportType !== "unknown";
  const hasAllRegionTypes =
    !isMultiRegionPreview ||
    allRegionReportTypesSet(worksheets, regionReportTypes, mappingSelection);
  const enforceReportType = requiresReportType;
  const enforceSingleReportType = enforceReportType && !isMultiRegionPreview;
  const enforceRegionReportTypes = enforceReportType && isMultiRegionPreview;
  const showRequiredWarning =
    enforceSingleReportType && !isClassifying && !hasConfirmedType && detectedType === "unknown";

  const showGlobalConfidence = globalConfidence !== null;

  const reviewBlockedMessage =
    globalConfidence !== null
      ? t("upload.ingestionConfidenceReviewBlocked", {
          score: Math.round(globalConfidence * 100),
          minimum: Math.round(reviewThreshold * 100),
        })
      : reviewReason;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid="confirm-structure-panel">
      <div className="space-y-2 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <p className="min-w-0 font-mono text-sm font-semibold text-[var(--color-text-primary)] truncate">
              {fileName}
            </p>
            <span className="text-[var(--color-text-tertiary)] hidden sm:inline" aria-hidden>
              ·
            </span>
            <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
              <span>{t("upload.targetSchema")}:</span>
              <span className="schema-chip text-[10px] font-semibold text-[var(--color-text-secondary)] bg-[var(--color-bg-secondary)] border border-[var(--color-border-light)] rounded px-2 py-0.5">
                {schemaName}
              </span>
            </span>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1">
            {showGlobalConfidence ? (
              <span
                data-testid="global-confidence-score"
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-mono text-xs font-semibold tabular-nums",
                  "bg-[var(--color-bg-tertiary)]",
                  globalConfidenceColorClass(globalConfidence!)
                )}
              >
                {t("upload.globalConfidenceValue", {
                  percent: Math.round(globalConfidence! * 100),
                })}
              </span>
            ) : null}
            <span className="text-xs text-[var(--color-text-tertiary)]">
              {formatDateTime(new Date())}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="structure-summary-tiles">
          <div className="rounded-md border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-3 py-2 min-w-[88px]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
              {t("upload.confirmStructureTablesSelected")}
            </p>
            <p className="font-mono text-lg font-semibold text-[var(--color-text-primary)]">
              {selectedCount}
            </p>
          </div>
          <div className="rounded-md border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-3 py-2 min-w-[88px]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
              {t("upload.confirmStructureRowsSelected")}
            </p>
            <p className="font-mono text-lg font-semibold">{selectedRowCount.toLocaleString()}</p>
          </div>
          <div className="rounded-md border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-3 py-2 min-w-[88px]">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
              {t("upload.confirmStructureSkipped")}
            </p>
            <p className="font-mono text-lg font-semibold text-[var(--color-text-tertiary)]">
              {skippedCount}
            </p>
          </div>
        </div>

        {enforceSingleReportType && (
          <div className="flex items-center gap-3 pt-1">
            <label className="text-sm font-semibold text-[var(--color-text-primary)] whitespace-nowrap">
              {t("upload.fileTypeLabel")}:
            </label>
            <div className="flex-1 max-w-xs" data-testid="file-type-select">
              <SelectField
                value={reportType ?? ""}
                onValueChange={(v) => onReportTypeChange?.(v || null)}
                options={fileTypeOptions}
                placeholder={
                  isClassifying ? t("upload.fileTypeClassifying") : t("upload.fileTypePlaceholder")
                }
                disabled={isClassifying || fileTypeOptions.length === 0 || isUploading}
              />
            </div>
            {isClassifying && (
              <span className="flex items-center gap-1.5 text-xs text-[var(--color-text-tertiary)]">
                <Spinner size="sm" />
                {t("upload.fileTypeClassifying")}
              </span>
            )}
          </div>
        )}

        {showRequiredWarning && (
          <p className="text-xs text-[var(--color-status-error,#b91c1c)]">
            {t("upload.fileTypeRequired")}
          </p>
        )}

        {ingestionTriggerSkipped ? (
          <p className="text-xs text-[var(--color-text-secondary)]" data-testid="ingestion-summary">
            <span data-testid="ingestion-reused">{t("upload.ingestionReusedExisting")}</span>
          </p>
        ) : null}

        {requiresReview && reviewBlockedMessage ? (
          <p
            className="text-xs text-[var(--color-status-error,#b91c1c)]"
            data-testid="confidence-review-warning"
          >
            {reviewBlockedMessage}
          </p>
        ) : null}

        {selectedCount === 0 ? (
          <p
            className="text-xs text-[var(--color-status-error,#b91c1c)]"
            data-testid="no-tables-selected"
          >
            {t("upload.confirmStructureSelectAtLeastOne")}
          </p>
        ) : null}

        {enforceRegionReportTypes && !hasAllRegionTypes ? (
          <p className="text-xs text-[var(--color-status-error,#b91c1c)]">
            {t("upload.regionReportTypesRequired")}
          </p>
        ) : null}
      </div>

      <div className="shrink-0 space-y-1">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t("upload.confirmStructureRegionsHeading")}
        </h3>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t("upload.confirmStructureRegionsHint")}
        </p>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto space-y-2 pr-1"
        data-testid="structure-table-list"
      >
        {worksheets.map((ws, index) => {
          const previewKey = getWorksheetKey(ws, index);
          const included = isWorksheetSelected(ws, index, mappingSelection);
          const expanded = expandedKeys.has(previewKey);
          const kind = sectionKindLabel(ws);
          const showPromotedWarning = included && (kind === "non-mapping" || kind === "ambiguity");
          const tableLabel =
            kind === "mapping" && ws.sheetNames && ws.sheetNames.length > 0
              ? t("upload.tableOrdinalWithSheetsLabel", {
                  n: index + 1,
                  sheets: ws.sheetNames.join(", "),
                })
              : t("upload.tableOrdinalLabel", { n: index + 1 });

          return (
            <article
              key={previewKey}
              className={cn(
                "rounded-lg border bg-[var(--color-bg-primary)] shadow-sm transition-colors",
                included
                  ? "border-[var(--color-border)]"
                  : "border-[var(--color-border-light)] opacity-80"
              )}
              data-testid={`structure-card-${previewKey}`}
            >
              <div className="flex items-center gap-3 p-3">
                <RegionIncludeCheckbox
                  previewKey={previewKey}
                  included={included}
                  ariaLabel={t("upload.tableIncludeInMapping")}
                  onToggle={() => onMappingSelectionChange(previewKey, !included)}
                />

                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => toggleExpanded(previewKey)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-[var(--color-text-primary)] truncate">
                      {tableLabel}
                    </span>
                    <span
                      className={cn(
                        "px-1.5 py-0.5 text-[10px] rounded font-medium",
                        kind === "mapping"
                          ? "bg-[var(--color-brand-primary)]/12 text-[var(--color-brand-primary-dark)]"
                          : kind === "ambiguity"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]"
                      )}
                    >
                      {kind}
                    </span>
                    <span className="font-mono text-[11px] text-[var(--color-text-tertiary)]">
                      {ws.totalRows.toLocaleString()} rows
                    </span>
                  </div>
                  <p className="mt-1 text-[10px] font-medium text-[var(--color-text-secondary)]">
                    {included
                      ? t("upload.tableIncludedInMapping")
                      : t("upload.tableExcludedFromMapping")}
                  </p>
                </button>

                <button
                  type="button"
                  className="shrink-0 self-start rounded-full p-0.5 text-[var(--color-text-tertiary)] transition-colors hover:text-[var(--color-text-primary)]"
                  onClick={() => toggleExpanded(previewKey)}
                  aria-expanded={expanded}
                  aria-label={expanded ? "Collapse section" : "Expand section"}
                  data-testid={`structure-toggle-${previewKey}`}
                >
                  {expanded ? (
                    <CircleChevronUp className="h-5 w-5" aria-hidden />
                  ) : (
                    <CircleChevronDown className="h-5 w-5" aria-hidden />
                  )}
                </button>
              </div>

              {showPromotedWarning ? (
                <div className="mx-3 mb-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                  {t("upload.confirmStructurePromotedWarning")}
                </div>
              ) : null}

              {included && enforceRegionReportTypes && (
                <div className="px-3 pb-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {t("upload.regionReportTypeLabel", { table: tableLabel })}
                  </span>
                  <div className="flex-1 max-w-xs" data-testid={`region-report-type-${previewKey}`}>
                    <SelectField
                      value={regionReportTypes[previewKey] ?? ""}
                      onValueChange={(v) => onRegionReportTypeChange?.(previewKey, v || null)}
                      options={fileTypeOptions}
                      placeholder={t("upload.fileTypePlaceholder")}
                      disabled={isClassifying || fileTypeOptions.length === 0 || isUploading}
                    />
                  </div>
                </div>
              )}

              {expanded ? (
                <div className="border-t border-[var(--color-border-light)] px-2 pb-2">
                  <VirtualTable
                    headers={ws.headers}
                    rows={ws.rows}
                    totalRows={ws.totalRows}
                    maxHeight="220px"
                  />
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}

/** Derived confirm disabled state for the parent dialog footer. */
// eslint-disable-next-line react-refresh/only-export-components
export function isConfirmStructureBlocked(params: {
  isUploading: boolean;
  isConfirming: boolean;
  isClassifying: boolean;
  requiresReview: boolean;
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  requiresReportType: boolean;
  reportType: string | null;
  regionReportTypes: Record<string, string | null>;
}): boolean {
  const selectedCount = countSelectedMappingWorksheets(params.worksheets, params.mappingSelection);
  const selected = getSelectedMappingWorksheets(params.worksheets, params.mappingSelection);
  const isMulti = selected.length > 1;
  const hasConfirmedType = !!params.reportType && params.reportType !== "unknown";
  const hasAllRegionTypes =
    !isMulti ||
    allRegionReportTypesSet(params.worksheets, params.regionReportTypes, params.mappingSelection);
  const enforceReportType = params.requiresReportType;
  const enforceSingle = enforceReportType && !isMulti;
  const enforceRegions = enforceReportType && isMulti;

  if (params.isUploading || params.isConfirming || params.requiresReview || selectedCount < 1) {
    return true;
  }
  if (enforceRegions) {
    return params.isClassifying || !hasAllRegionTypes;
  }
  if (enforceSingle) {
    return params.isClassifying || !hasConfirmedType;
  }
  return false;
}

// eslint-disable-next-line react-refresh/only-export-components
export function confirmStructurePrompt(params: {
  isUploading: boolean;
  requiresReview: boolean;
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  requiresReportType: boolean;
  reportType: string | null;
  regionReportTypes: Record<string, string | null>;
  t: (key: string) => string;
}): string {
  const { t } = params;
  if (params.isUploading) {
    return "Loading extraction preview...";
  }
  if (params.requiresReview) {
    return "Review required before you can proceed";
  }
  const selected = getSelectedMappingWorksheets(params.worksheets, params.mappingSelection);
  const isMulti = selected.length > 1;
  const hasConfirmedType = !!params.reportType && params.reportType !== "unknown";
  const hasAllRegionTypes =
    !isMulti ||
    allRegionReportTypesSet(params.worksheets, params.regionReportTypes, params.mappingSelection);
  if (params.requiresReportType && isMulti && !hasAllRegionTypes) {
    return t("upload.regionReportTypesRequired");
  }
  if (params.requiresReportType && !isMulti && !hasConfirmedType) {
    return t("upload.fileTypeRequired");
  }
  if (countSelectedMappingWorksheets(params.worksheets, params.mappingSelection) < 1) {
    return t("upload.confirmStructureSelectAtLeastOne");
  }
  return t("upload.confirmStructureQuestion");
}
